/**
 * Year selector ranges from the oldest and newest dated row, rather than from a list of
 * every row (which the PostgREST row cap truncates, dropping the oldest years from the
 * selector and making their data unreachable).
 *
 * Pure, no Supabase import: tested in src/lib/__tests__/yearRange.test.ts.
 */

export interface YearBounds {
  minYear: number;
  maxYear: number;
}

/**
 * How far from the current year the data may widen a selector. A receipt whose year the
 * OCR misread (1926, 2062) would otherwise stretch it across decades.
 */
export const YEAR_LOOKBACK = 15;
export const YEAR_LOOKAHEAD = 1;

/** The year of a `YYYY-MM-DD` (or ISO timestamp) string, or null. */
export function yearOf(date: string | null | undefined): number | null {
  if (!date) return null;
  const year = Number(date.slice(0, 4));
  return Number.isInteger(year) && year > 0 ? year : null;
}

/** Bounds from the oldest and newest dates, or null when there is no dated row at all. */
export function yearBoundsFromDates(
  oldest: string | null | undefined,
  newest: string | null | undefined
): YearBounds | null {
  const first = yearOf(oldest) ?? yearOf(newest);
  if (first === null) return null;
  const last = yearOf(newest) ?? first;
  return { minYear: Math.min(first, last), maxYear: Math.max(first, last) };
}

/** A year clamped to what a selector may offer. */
export function clampYear(year: number, currentYear = new Date().getFullYear()): number {
  return Math.min(Math.max(year, currentYear - YEAR_LOOKBACK), currentYear + YEAR_LOOKAHEAD);
}

/**
 * Every year from the newest to the oldest, descending and contiguous: the data's bounds
 * (clamped, see YEAR_LOOKBACK) plus the years a page always offers (`include`, not
 * clamped). Never empty — with no bounds and nothing to include it is the current year.
 */
export function yearRange(
  bounds: YearBounds | null | undefined,
  include: readonly number[],
  currentYear = new Date().getFullYear()
): number[] {
  const years = include.filter((y) => Number.isInteger(y));
  if (bounds) {
    years.push(clampYear(bounds.minYear, currentYear), clampYear(bounds.maxYear, currentYear));
  }
  if (years.length === 0) years.push(currentYear);

  const newest = Math.max(...years);
  const oldest = Math.min(...years);
  return Array.from({ length: newest - oldest + 1 }, (_, i) => newest - i);
}
